/**
 * What a public hero asks for.
 *
 * Sign-up is the first step, so by default this is one button, Start free,
 * and one line saying what a free account is. The address box (`HomeScan`)
 * is kept whole and drawn only when the server says the switch is off, which
 * is the way back if sign-up first has to be turned off: nothing about the
 * box or its scan was changed to make room for this.
 *
 * One component, used at the top of a page and again at its close, so the
 * two cannot say different things.
 */
import HomeScan from '@/components/home/HomeScan'
import StartFree from '@/components/home/StartFree'
import { useSignupFirst } from '@/hooks/useSignupFirst'
import '@/design-system/components.css'

interface HeroEntryProps {
  /** Where this sits, for the click count. */
  placement: string
  /** The line under Start free. */
  startNote: string
  /** The line under the address box, when the switch is off. */
  scanNote: string
  /** On the closing band: the paper button, centred. */
  onBand?: boolean
}

export default function HeroEntry({ placement, startNote, scanNote, onBand = false }: HeroEntryProps) {
  const signupFirst = useSignupFirst()

  if (!signupFirst) {
    return (
      <>
        <HomeScan />
        <p className="ds-small ds-muted ds-strong" style={{ marginTop: 12, paddingLeft: 8 }}>
          {scanNote}
        </p>
      </>
    )
  }

  return (
    <div
      data-testid="hero-entry"
      style={{ display: 'flex', flexDirection: 'column', alignItems: onBand ? 'center' : 'flex-start', gap: 12 }}
    >
      <StartFree placement={placement} variant={onBand ? 'secondary' : 'primary'} />
      {/* Centred on the band, where the button is: the line is wider than a
          phone's column and wraps, and a second row at the left edge under a
          centred button reads as a mistake. */}
      <p className="ds-small ds-muted ds-strong" style={{ margin: 0, textAlign: onBand ? 'center' : undefined }}>
        {startNote}
      </p>
    </div>
  )
}
